import React from 'react';
import { useQuery } from '@apollo/client';
import Link from 'nextDir/modules/Router/Link';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import {
  isCountryCodeVN,
  useCountryData,
} from 'src/common/hooks/useCountryData';
import { ROUTES } from 'src/common/routes';
import GlintsContainer from 'src/components/GlintsContainer';
import { MetadataRenderer } from 'src/components/MetaDescription/MetadataRenderer';
import { HierarchicalLocation } from 'src/modules/Profile/graphql/hierarchicalLocations';
import {
  GetActiveJobsArg,
  GetDistrictsWithActiveJobsData,
  queryDistrictsWithActiveJobs,
} from 'src/modules/Seo/graphql';
import * as S from 'src/modules/Seo/JobsIndexPage.sc';
import { City, GroupedLocation, Province } from 'src/modules/Seo/type';

const JobsByNewLocation: React.FC<
  React.PropsWithChildren<{
    initialDistrictsData: GetDistrictsWithActiveJobsData;
  }>
> = ({ initialDistrictsData }) => {
  const intl = useIntl();
  const { countryCode, englishCountryName } = useCountryData();

  const shouldSkipQuery =
    initialDistrictsData?.getDistrictsWithActiveJobs?.locations?.length > 0;

  const { data } = useQuery<GetDistrictsWithActiveJobsData, GetActiveJobsArg>(
    queryDistrictsWithActiveJobs,
    {
      variables: {
        countryCode,
        shouldGetAllData: true,
      },
      skip: shouldSkipQuery,
    }
  );

  const _isCountryCodeVN = isCountryCodeVN(countryCode);

  const districtData = shouldSkipQuery ? initialDistrictsData : data;

  const groupedData: GroupedLocation = districtData
    ? groupByLocation(
        districtData.getDistrictsWithActiveJobs.locations,
        _isCountryCodeVN
      ).sort((a, b) => a.name.localeCompare(b.name))
    : [];

  return (
    <>
      <MetadataRenderer
        title={intl.formatMessage(messages.title, {
          country: englishCountryName,
        })}
        description={intl.formatMessage(messages.description, {
          country: englishCountryName,
        })}
        glintsTrackingName="explore"
      />

      <GlintsContainer>
        <S.Header>
          <FormattedMessage
            {...messages.heading}
            values={{ country: englishCountryName }}
          />
        </S.Header>

        {groupedData && groupedData.length > 0 && (
          <>
            <S.ListContainer>
              {groupedData.map((province) => {
                return (
                  <S.OuterItem key={province.id}>
                    {/* Province */}
                    <ProvinceComponent province={province} />
                    <S.CustomHr />
                    {province.cities.map((city) => {
                      return (
                        <>
                          <S.MiddleContainer>
                            {/* City */}
                            <CityComponent
                              key={city.id}
                              city={city}
                              province={province}
                            />

                            {/* District */}
                            {!_isCountryCodeVN && (
                              <S.InnerContainer>
                                {city.districts.map((district) => (
                                  <DistrictComponent
                                    key={district.id}
                                    district={district}
                                    province={province}
                                    city={city}
                                  />
                                ))}
                              </S.InnerContainer>
                            )}
                          </S.MiddleContainer>
                        </>
                      );
                    })}
                  </S.OuterItem>
                );
              })}
            </S.ListContainer>
          </>
        )}
      </GlintsContainer>
    </>
  );
};

const messages = defineMessages({
  title: {
    id: 'jobs-by_new-location-pages_meta_title',
    defaultMessage:
      'Browse Jobs by Locations in {country} - Jobs Near You | Glints',
  },
  description: {
    id: 'jobs-by_new-location-pages_meta_description',
    defaultMessage:
      'Browse job vacancies near you! Discover jobs opportunities in various location in {country} with Glints: Career Discovery without Limits',
  },
  heading: {
    id: 'jobs-by_new-location-pages_meta_heading',
    defaultMessage: 'Browse Jobs In {country} by City / Location',
  },
});

function groupByLocation(
  locations: HierarchicalLocation[],
  isCountryCodeVN: boolean
): GroupedLocation {
  if (!locations || (Array.isArray(locations) && locations.length === 0)) {
    return [];
  }

  function createCityObject(
    city: HierarchicalLocation,
    location: HierarchicalLocation
  ): City {
    return {
      id: city.id,
      name: city.name,
      formattedName: city.formattedName,
      slug: city.slug,
      districts: [location],
    };
  }

  return locations.reduce((groupedLocations: GroupedLocation, location) => {
    const country = location.parents.find((parent) => parent.level === 1);
    const province = location.parents.find((parent) => parent.level === 2);
    const city = isCountryCodeVN
      ? location
      : location.parents.find((parent) => parent.level === 3);

    if (!country || !province || !city) {
      return groupedLocations;
    }

    const existingProvince = groupedLocations.find((e) => e.id === province.id);

    if (!existingProvince) {
      const provinceDetails: Province = {
        id: province.id,
        name: province.name,
        formattedName: province.formattedName,
        slug: province.slug,
        country: country.slug,
        cities: [createCityObject(city, location)],
      };
      return [...groupedLocations, provinceDetails];
    }

    const existingCity = existingProvince.cities.find((c) => c.id === city.id);

    if (!existingCity) {
      const newCity = createCityObject(city, location);
      existingProvince.cities.push(newCity);
    } else {
      existingCity.districts.push(location);
    }

    return groupedLocations;
  }, []);
}

/** Components **/
const ProvinceComponent = ({ province }: { province: Province }) => {
  return (
    <Link
      href={`/${ROUTES.opportunitiesJobsExplore}`}
      as={`/job-location/${province.country}/${province.slug}`}
    >
      {province.formattedName}
    </Link>
  );
};

const CityComponent = ({
  city,
  province,
}: {
  city: City;
  province: Province;
}) => {
  return (
    <S.MiddleItem>
      <Link
        href={`/${ROUTES.opportunitiesJobsExplore}`}
        as={`/job-location/${province.country}/${province.slug}/${city.slug}`}
      >
        {city.formattedName}
      </Link>
    </S.MiddleItem>
  );
};

const DistrictComponent = ({
  district,
  province,
  city,
}: {
  district: HierarchicalLocation;
  province: Province;
  city: City;
}) => {
  return (
    <>
      <li>
        <Link
          key={district.id}
          href={`/${ROUTES.opportunitiesJobsExplore}`}
          as={`/job-location/${province.country}/${province.slug}/${city.slug}/${district.slug}`}
        >
          <h4>{district.formattedName}</h4>
        </Link>
      </li>
    </>
  );
};

export default React.memo(JobsByNewLocation);
